/**
 * Tiny color helpers shared between Reports preview, PDF and PPTX exporters.
 *
 * Everything works on hex strings (with or without `#`). RGB / luminance
 * functions exist so the `auto_contrast` logo style can pick a plate that
 * stays readable on the chosen brand color.
 */

const HEX_RE = /^#?([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** Default Relix accent when a product has no `brand_color` set. */
export const RELIX_DEFAULT_ACCENT = '#7C5CFF';

export function isValidHex(hex: string | null | undefined): boolean {
  return typeof hex === 'string' && HEX_RE.test(hex.trim());
}

export function normalizeHex(hex: string | null | undefined): string | null {
  if (!isValidHex(hex)) return null;
  let raw = (hex as string).trim().replace(/^#/, '');
  if (raw.length === 3) {
    raw = raw
      .split('')
      .map((c) => c + c)
      .join('');
  }
  return `#${raw.toUpperCase()}`;
}

export function hexToRgb(hex: string): Rgb {
  const n = normalizeHex(hex);
  if (!n) return { r: 0, g: 0, b: 0 };
  const value = n.slice(1);
  return {
    r: parseInt(value.slice(0, 2), 16),
    g: parseInt(value.slice(2, 4), 16),
    b: parseInt(value.slice(4, 6), 16),
  };
}

/**
 * YIQ-based luminance check. The threshold (128) is the classic accessible
 * contrast cut-off — anything below is considered "dark" and benefits from
 * a light plate around the logo.
 */
export function isColorDark(hex: string | null | undefined): boolean {
  const { r, g, b } = hexToRgb(hex || '#FFFFFF');
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq < 128;
}

/** Convenience for pptxgenjs / docx which prefer hex *without* `#`. */
export function hexNoHash(hex: string | null | undefined, fallback = '7C5CFF'): string {
  const n = normalizeHex(hex);
  return n ? n.slice(1) : fallback;
}

export type LogoBackgroundContext = 'cover' | 'header' | 'dark-surface' | 'light-surface';

/**
 * Resolves the practical logo style for the given context. The Auto setting
 * picks a plate intelligently:
 *   - on a dark cover band → light plate (logo stays readable)
 *   - on the white header / page body → transparent
 *   - on a dedicated dark surface → light plate
 *   - on a dedicated light surface → transparent
 */
export function resolveAutoContrast(
  configured: 'transparent' | 'light_plate' | 'dark_plate' | 'auto_contrast' | undefined,
  context: LogoBackgroundContext,
  brandColor: string | null | undefined
): 'transparent' | 'light_plate' | 'dark_plate' {
  if (configured && configured !== 'auto_contrast') return configured;
  switch (context) {
    case 'cover':
      return isColorDark(brandColor || RELIX_DEFAULT_ACCENT) ? 'light_plate' : 'transparent';
    case 'dark-surface':
      return 'light_plate';
    case 'light-surface':
    case 'header':
    default:
      return 'transparent';
  }
}
